import type { ImporterConfig, PersistedImportState } from './types'

export const DEFAULT_PAGE_SIZE = 100
export const DEFAULT_SLOW_QUEUE_LIMIT = 25

export function getCurrentImporterYear(): number {
    return new Date().getFullYear()
}

export function getDefaultImporterDateRange() {
    const currentYear = getCurrentImporterYear()

    return {
        fromYear: currentYear,
        fromMonth: 1,
        toYear: currentYear,
        toMonth: 12,
        fromDay: 1,
        toDay: 31,
    }
}

export function getImporterYearOptions(startYear = 2020): number[] {
    const currentYear = getCurrentImporterYear()
    return Array.from(
        { length: currentYear - startYear + 1 },
        (_, index) => startYear + index,
    )
}

export const DEFAULT_IMPORTER_CONFIG: ImporterConfig = {
    terms: '',
    dateRange: getDefaultImporterDateRange(),
    selectedLanguages: ['en'],
    pageSize: DEFAULT_PAGE_SIZE,
}

export const DEFAULT_IMPORTER_STATE: PersistedImportState = {
    startedAt: null,
    lastUpdatedAt: null,
    nextPageToken: undefined,
    status: {
        paused: false,
        scan: 'idle',
        import: 'idle',
    },
    errorMessage: null,
    lastMessageTimestamp: null,
    totalInvoiceCandidates: 0,
    actualInvoicesFoundThisRun: 0,
    invoiceCandidatesSeenThisRun: 0,
    invoiceCandidatesProcessedThisRun: 0,
    config: DEFAULT_IMPORTER_CONFIG,
}

export const TAX_DEADLINES_BY_COUNTRY: TaxDeadlinesByCountry = {
    US: {
        code: 'US',
        fiscalYear: {
            isFixedFiscalYear: false,
            start: { day: 1, month: 1 },
            end: { day: 31, month: 12 },
            defaultCalendar: true,
        },
        filingDeadlines: [
            {
                type: 'withoutAccountant',
                offsetMonths: 4,
                targetDay: 15,
                notes: '15th day of 4th month after year-end',
            },
        ],
        notes: 'Quarterly estimates apply.',
    },
    CA: {
        code: 'CA',
        fiscalYear: {
            isFixedFiscalYear: false,
            start: { day: 1, month: 1 },
            end: { day: 31, month: 12 },
            defaultCalendar: true,
        },
        filingDeadlines: [
            {
                type: 'standard',
                offsetMonths: 6,
                targetDay: 30, // Assumed end-of-month for June 30 example
            },
        ],
        notes: 'Varies by fiscal year; 6 months after end.',
    },
    MX: {
        code: 'MX',
        fiscalYear: {
            isFixedFiscalYear: true,
            start: { day: 1, month: 1 },
            end: { day: 31, month: 12 },
        },
        filingDeadlines: [
            {
                type: 'withoutAccountant',
                offsetMonths: 3,
                targetDay: 31,
            },
        ],
    },
    GB: {
        code: 'GB',
        fiscalYear: {
            isFixedFiscalYear: true,
            start: { day: 6, month: 4 },
            end: { day: 5, month: 4 },
        },
        filingDeadlines: [
            {
                type: 'standard',
                offsetMonths: 6,
                targetDay: 31,
                notes: 'Filing due October 31 after fiscal year end.',
            },
        ],
        notes: 'Fiscal year varies by company accounting period; this assumes standard April 6 to April 5 tax year.',
    },
    DE: {
        code: 'DE',
        fiscalYear: {
            isFixedFiscalYear: true,
            start: { day: 1, month: 1 },
            end: { day: 31, month: 12 },
        },
        filingDeadlines: [
            {
                type: 'withoutAccountant',
                offsetMonths: 7,
                targetDay: 31,
            },
            {
                type: 'withAccountant',
                offsetMonths: 14,
                targetDay: 28,
                notes: 'Extended for tax advisers.',
            },
        ],
    },
    FR: {
        code: 'FR',
        fiscalYear: {
            isFixedFiscalYear: false,
            start: { day: 1, month: 1 },
            end: { day: 31, month: 12 },
            defaultCalendar: true,
        },
        filingDeadlines: [
            {
                type: 'withoutAccountant',
                offsetMonths: 3,
                targetDay: 31,
            },
        ],
        notes: 'End of 3rd month; May 31 for Dec 31 end.',
    },
    ES: {
        code: 'ES',
        fiscalYear: {
            isFixedFiscalYear: true,
            start: { day: 1, month: 1 },
            end: { day: 31, month: 12 },
        },
        filingDeadlines: [
            {
                type: 'withoutAccountant',
                offsetMonths: 6,
                targetDay: 25, // 25 days into the 7th month
            },
        ],
    },
    CN: {
        code: 'CN',
        fiscalYear: {
            isFixedFiscalYear: true,
            start: { day: 1, month: 1 },
            end: { day: 31, month: 12 },
        },
        filingDeadlines: [
            {
                type: 'withoutAccountant',
                offsetMonths: 5,
                targetDay: 30,
            },
        ],
    },
    JP: {
        code: 'JP',
        fiscalYear: {
            isFixedFiscalYear: false,
            start: { day: 1, month: 1 },
            end: { day: 31, month: 12 },
            defaultCalendar: true,
        },
        filingDeadlines: [
            {
                type: 'standard',
                offsetMonths: 2,
                targetDay: 28, // End-of-month assumption (Feb 28/March 1)
            },
        ],
        notes: 'Varies by fiscal end.',
    },
    IN: {
        code: 'IN',
        fiscalYear: {
            isFixedFiscalYear: true,
            start: { day: 1, month: 4 },
            end: { day: 31, month: 3 },
        },
        filingDeadlines: [
            {
                type: 'withoutAccountant',
                offsetMonths: 7,
                targetDay: 31,
                notes: 'Extended to Nov 30 for audits.',
            },
        ],
    },
    SG: {
        code: 'SG',
        fiscalYear: {
            isFixedFiscalYear: false,
            start: { day: 1, month: 1 },
            end: { day: 31, month: 12 },
            defaultCalendar: true,
        },
        filingDeadlines: [
            {
                type: 'withoutAccountant',
                offsetMonths: 11,
                targetDay: 30,
            },
        ],
    },
    AE: {
        code: 'AE',
        fiscalYear: {
            isFixedFiscalYear: false,
            start: { day: 1, month: 1 },
            end: { day: 31, month: 12 },
            defaultCalendar: true,
        },
        filingDeadlines: [
            {
                type: 'standard',
                offsetMonths: 9,
                targetDay: 30,
            },
        ],
    },
    SA: {
        code: 'SA',
        fiscalYear: {
            isFixedFiscalYear: false,
            start: { day: 1, month: 1 },
            end: { day: 31, month: 12 },
            defaultCalendar: true,
        },
        filingDeadlines: [
            {
                type: 'standard',
                offsetMonths: 4, // 120 days ≈ 4 months
                targetDay: 30,
            },
        ],
    },
    AU: {
        code: 'AU',
        fiscalYear: {
            isFixedFiscalYear: true,
            start: { day: 1, month: 7 },
            end: { day: 30, month: 6 },
        },
        filingDeadlines: [
            {
                type: 'withoutAccountant',
                offsetMonths: 7,
                targetDay: 15,
            },
        ],
        notes: '15th of 7th month after year-end.',
    },
    NZ: {
        code: 'NZ',
        fiscalYear: {
            isFixedFiscalYear: true,
            start: { day: 1, month: 4 },
            end: { day: 31, month: 3 },
        },
        filingDeadlines: [
            {
                type: 'withoutAccountant',
                offsetMonths: 3,
                targetDay: 7, // July 7 for March 31 end
                targetMonthAdjustment: 4, // Adjust to July
            },
        ],
    },
    // Add others as needed (e.g., IL: { ... } with April 30 if business details emerge)
} as const

export type Month = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
export type Day =
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6
    | 7
    | 8
    | 9
    | 10
    | 11
    | 12
    | 13
    | 14
    | 15
    | 16
    | 17
    | 18
    | 19
    | 20
    | 21
    | 22
    | 23
    | 24
    | 25
    | 26
    | 27
    | 28
    | 29
    | 30
    | 31 // Enum or union for validation

export type DeadlineVariant = {
    type: 'withoutAccountant' | 'withAccountant' | 'standard' // 'standard' for no variant
    offsetMonths: number // Months after fiscal year-end (e.g., 7 for DE July 31)
    targetDay: Day // Fixed day in the resulting month (e.g., 31)
    targetMonthAdjustment?: number // Optional month shift if offset lands wrong (rare)
    notes?: string
}

export type FiscalYearConfig = {
    isFixedFiscalYear: boolean // true for mandatory non-calendar (e.g., UK); false for calendar default
    start: { day: Day; month: Month } // e.g., { day: 6, month: 4 } for UK
    end: { day: Day; month: Month } // e.g., { day: 5, month: 4 } for UK (next year)
    defaultCalendar?: boolean // true if flexible but calendar assumed
}

export type CountryTaxConfig = {
    code: string // ISO code, e.g., 'GB'
    fiscalYear: FiscalYearConfig
    filingDeadlines: DeadlineVariant[] // Array for variants
    notes?: string
}

export type TaxDeadlinesByCountry = Record<string, CountryTaxConfig>
